import {
  HttpClient,
  HttpParams,
  HttpErrorResponse,
} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";

import { Observable, throwError } from "rxjs";

import { catchError, map } from "rxjs/operators";

import {
  ApiResponse,
  ApiResponseError,
  PaginatedApiResponse
} from "@lib/types";
import {
  AppConfigService,
  MicroserviceType,
} from "@app/shared/services/app-config.service";

export interface RequestOptions {
  params?: HttpParams | { [param: string]: string | string[] };
  headers?: { [header: string]: string | string[] };
}

@Injectable({
  providedIn: "root",
})
export abstract class BaseApiService {
  protected abstract readonly endpoint: string;
  protected abstract readonly microservice: MicroserviceType;

  protected readonly http = inject(HttpClient);
  protected readonly configService = inject(AppConfigService);

  protected get apiUrl(): string {
    return `${this.configService.getApiUrl(this.microservice)}${this.endpoint}`;
  }

  /**
   * Generic GET request that handles ApiResponse wrapping
   */
  protected get<T>(
    path: string = "",
    options: RequestOptions = {},
  ): Observable<T> {
    const url = path ? `${this.apiUrl}${path}` : this.apiUrl;

    return this.executeRequest<T>(() =>
      this.http.get<ApiResponse<T>>(url, {
        params: options.params,
        headers: options.headers,
      }),
    );
  }

  /**
   * Generic GET request for paginated responses
   */
  protected getPaginated<T>(
    path: string = "",
    options: RequestOptions = {},
  ): Observable<PaginatedApiResponse<T>> {
    const url = path ? `${this.apiUrl}${path}` : this.apiUrl;

    return this.http
      .get<PaginatedApiResponse<T>>(url, {
        params: options.params,
        headers: options.headers,
      })
      .pipe(
        map((response: PaginatedApiResponse<T>) => {
          if (!response.success) {
            throw new ApiResponseError(
              response.message ?? "API request failed",
              response.errors ?? []
            );
          }
          return response;
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Generic POST request that handles ApiResponse wrapping
   */
  protected post<T, R = T>(
    body: R,
    path: string = "",
    options: RequestOptions = {},
  ): Observable<T> {
    const url = path ? `${this.apiUrl}${path}` : this.apiUrl;

    return this.executeRequest<T>(() =>
      this.http.post<ApiResponse<T>>(url, body, {
        params: options.params,
        headers: options.headers,
      }),
    );
  }

  /**
   * Generic PUT request that handles ApiResponse wrapping
   */
  protected put<T, R = T>(
    body: R,
    path: string = "",
    options: RequestOptions = {},
  ): Observable<T> {
    const url = path ? `${this.apiUrl}${path}` : this.apiUrl;

    return this.executeRequest<T>(() =>
      this.http.put<ApiResponse<T>>(url, body, {
        params: options.params,
        headers: options.headers,
      }),
    );
  }

  /**
   * Generic DELETE request that handles ApiResponse wrapping
   */
  protected delete<T = void>(
    path: string = "",
    options: RequestOptions = {},
  ): Observable<T> {
    const url = path ? `${this.apiUrl}${path}` : this.apiUrl;

    return this.executeRequest<T>(() =>
      this.http.delete<ApiResponse<T>>(url, {
        params: options.params,
        headers: options.headers,
      }),
    );
  }

  /**
   * Generic DELETE request with body
   */
  protected deleteWithBody<T = void, R = any>(
    body: R,
    path: string = "",
    options: RequestOptions = {},
  ): Observable<T> {
    const url = path ? `${this.apiUrl}${path}` : this.apiUrl;

    return this.executeRequest<T>(() =>
      this.http.delete<ApiResponse<T>>(url, {
        body,
        params: options.params,
        headers: options.headers,
      }),
    );
  }

  /**
   * Execute request with common error handling
   */
  private executeRequest<T>(
    requestFn: () => Observable<ApiResponse<T>>,
  ): Observable<T> {
    return requestFn().pipe(
      map((response: ApiResponse<T>) => {
        if (!response.success) {
          throw new ApiResponseError(
            response.message ?? "API request failed",
            response.errors ?? [],
          );
        }
        return response.data as T;
      }),
      catchError((error) => this.handleError(error)),
    );
  }

  /**
   * Centralized error handling
   */
  private handleError(error: HttpErrorResponse | Error): Observable<never> {
    return throwError(() => error);
  }

  /**
   * Helper method to build HttpParams from object
   */
  protected buildParams(params: { [key: string]: any }): HttpParams {
    let httpParams = new HttpParams();

    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value !== null && value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            httpParams = httpParams.append(key, item.toString());
          });
        } else {
          httpParams = httpParams.set(key, value.toString());
        }
      }
    });

    return httpParams;
  }
}
